/**
 * ThoughtLeadershipSection — Abschnitt 7c der Startseite.
 *
 * Der Weg zur ausgearbeiteten Denkleistung: heute das Whitepaper «Smart Client
 * Relations». Steht nach den Erfolgsgeschichten und vor dem Abschluss-Block,
 * damit der Beweisbogen von Logos ueber Faelle zur Denkleistung laeuft.
 *
 * Textlink statt Knopf — die Regel vom 3.8.2026: «Termin vereinbaren» ist der
 * einzige primaere Knopf, was auf eine Unterseite fuehrt, ist ein Textlink.
 * Der Pfeil kommt aus `SectionTextLink` und bedeutet Ortswechsel (Entscheid
 * Jan, 11.8.2026).
 *
 * Warum das die entfernte `whitepaper`-Sektion NICHT zurueckholt: siehe
 * `content/section-thought-leadership.ts`.
 */

import React from "react";
import type { ThoughtLeadershipSectionProps } from "../../content/section-thought-leadership";
import { useLocalizedUrl } from "../../hooks";
import { SectionBadge, SectionTextLink } from "../ui";

export type ThoughtLeadershipProps = ThoughtLeadershipSectionProps & {
    /**
     * Sprungziel der Anker-Nav (12.8.2026) — diese Sektion ist Kapitel 6 der
     * Startseite. Kein `ChapterSection` darum: Sie ist EIN Absatz mit EINEM
     * Ausgang, da waere ein Gefaess mit eigener Ueberschrift eine Huelle um
     * nichts.
     */
    anchorId?: string;
    /**
     * Seit dem 16.9.2026 steht die Sektion im Kapitel «Ansatz» und bringt dort
     * weder eigenen Hintergrund noch eine zweite `h2` mit — dieselbe Schaltung
     * wie in den anderen Kapitel-Teilen.
     */
    nested?: boolean;
    /** Das Kapitel-Etikett, aus derselben Quelle wie die Nav-Beschriftung. */
    badge?: string;
};

export const ThoughtLeadershipSection: React.FC<ThoughtLeadershipProps> = ({
    title,
    description,
    linkText,
    linkHref,
    anchorId,
    badge,
    nested = false,
}) => {
    const localizedHref = useLocalizedUrl(linkHref);

    return (
        <section
            id={anchorId}
            data-testid="thought-leadership-section"
            className={nested ? "" : "scroll-mt-24 bg-white pb-24"}
        >
            <div className="mx-auto w-full max-w-3xl px-4 text-center md:px-12">
                {badge && <SectionBadge variant="dark">{badge}</SectionBadge>}
                {nested ? (
                    <h3 className="mb-4 text-2xl font-black text-gray-900 md:text-3xl">{title}</h3>
                ) : (
                    <h2 className="mb-4 text-3xl font-black text-gray-900 md:text-4xl">{title}</h2>
                )}
                <p className="mb-8 text-lg leading-relaxed text-gray-600">{description}</p>
                {/*
                    `flex justify-center` statt `text-center`: `SectionTextLink`
                    ist `inline-flex` und richtet sich nicht nach `text-align`
                    (dieselbe Stelle wie in `HowWeWorkSection`).
                */}
                <div className="flex justify-center">
                    <SectionTextLink to={localizedHref}>{linkText}</SectionTextLink>
                </div>
            </div>
        </section>
    );
};

export default ThoughtLeadershipSection;
